import React, { useState, useEffect } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Save,
  X,
  Users,
  Tag,
  RefreshCw,
  ChevronDown,
  Database,
  AlertTriangle,
  Zap,
} from "lucide-react";
import useWhitelistManager from "../../hooks/useWhitelistManager";
import { supabase } from "../../lib/supabase";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Alert from "../ui/Alert";
import DeleteConfirmModal from "../ui/DeleteConfirmModal";

const WhitelistManager = () => {
  const {
    isLoading,
    error,
    success,
    clearMessages,
    getExistingAffiliateNames,
    getWhitelist,
    addToWhitelist,
    updateWhitelist,
    deleteFromWhitelist,
    toggleActive,
    manualReassignAds,
    rebuildEarningSummary,
    rebuildAllEarningSummaries,
  } = useWhitelistManager();

  // State management
  const [whitelist, setWhitelist] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    affiliate_name: "",
    patterns: "",
    autoReassign: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [existingAffiliates, setExistingAffiliates] = useState([]);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [rebuildingAll, setRebuildingAll] = useState(false);
  const [rebuildingIndividual, setRebuildingIndividual] = useState(new Set());

  // Load whitelist and existing affiliates on component mount
  useEffect(() => {
    loadWhitelist();
    loadExistingAffiliates();
  }, []);

  // Add periodic refresh to catch updates from other components
  useEffect(() => {
    const interval = setInterval(() => {
      loadWhitelist();
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(interval);
  }, []);

  const loadExistingAffiliates = async () => {
    const affiliates = await getExistingAffiliateNames();
    setExistingAffiliates(affiliates);
  };

  const loadWhitelist = async () => {
    const data = await getWhitelist();
    setWhitelist(data);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const patterns = formData.patterns
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p);

      await addToWhitelist(
        formData.affiliate_name,
        patterns,
        formData.autoReassign
      );
      await loadWhitelist();
      setShowAddForm(false);
      setFormData({ affiliate_name: "", patterns: "", autoReassign: true });
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      affiliate_name: item.affiliate_name,
      patterns: item.patterns ? item.patterns.join(", ") : "",
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const patterns = formData.patterns
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p);

      await updateWhitelist(
        editingId,
        {
          affiliate_name: formData.affiliate_name,
          patterns: patterns,
        },
        formData.autoReassign
      );
      await loadWhitelist();
      setEditingId(null);
      setFormData({ affiliate_name: "", patterns: "", autoReassign: true });
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleDelete = async (id) => {
    setItemToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      await deleteFromWhitelist(itemToDelete);
      await loadWhitelist();
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await toggleActive(id, !currentStatus);
      await loadWhitelist();
    } catch (err) {
      // Error handled by hook
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setShowAddForm(false);
    setShowDropdown(false);
    setFormData({ affiliate_name: "", patterns: "", autoReassign: true });
    clearMessages();
  };

  const handleSelectExistingAffiliate = (affiliateName) => {
    setFormData((prev) => ({ ...prev, affiliate_name: affiliateName }));
    setShowDropdown(false);
  };

  const handleManualReassign = async (affiliateName, patterns) => {
    try {
      await manualReassignAds(affiliateName, patterns);
      await loadWhitelist();
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleRebuildSummary = async (affiliateName) => {
    try {
      setRebuildingIndividual((prev) => new Set(prev).add(affiliateName));
      await rebuildEarningSummary(affiliateName);
      await loadWhitelist();
    } catch (err) {
      // Error handled by hook
    } finally {
      setRebuildingIndividual((prev) => {
        const newSet = new Set(prev);
        newSet.delete(affiliateName);
        return newSet;
      });
    }
  };

  const handleRebuildAllSummaries = async () => {
    try {
      setRebuildingAll(true);
      clearMessages();

      // Step 1: Rebuild pattern cache first
      console.log("🔄 Rebuilding pattern cache...");
      const { data: cacheData, error: cacheError } = await supabase.rpc(
        "rebuild_affiliate_pattern_cache"
      );

      if (cacheError) {
        throw new Error(`Pattern cache rebuild failed: ${cacheError.message}`);
      }

      const recordsProcessed = cacheData || 0;
      console.log(
        `✅ Pattern cache rebuilt successfully! Processed ${recordsProcessed} patterns.`
      );

      // Step 2: Rebuild all earning summaries
      // TODO: sanity check if there is anywhere else still referencing the earning summary table
      // if not, we can remove this.
      console.log("🔄 Rebuilding all earning summaries...");
      await rebuildAllEarningSummaries();

      await loadWhitelist();

      // Show success message
      alert(
        `✅ Complete rebuild successful!\n\nPattern cache: ${recordsProcessed} patterns processed\nEarning summaries: All affiliates updated`
      );
    } catch (err) {
      console.error("Error during complete rebuild:", err);
      alert(`❌ Rebuild failed: ${err.message}`);
    } finally {
      setRebuildingAll(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-2xl font-bold text-white">Affiliate Whitelist</h3>
          <p className="text-gray-300 text-sm sm:text-base">
            Manage which affiliates can be automatically assigned when importing
            CSV data
          </p>
        </div>
        <div className="flex gap-2 sm:gap-3 sm:self-end sm:ml-auto">
          <Button
            onClick={() => {
              loadWhitelist();
              loadExistingAffiliates();
            }}
            disabled={isLoading}
            className="bg-green-600 hover:bg-green-700 text-white"
            title="Refresh whitelist to show newly added affiliates"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh List
          </Button>
          <Button
            onClick={handleRebuildAllSummaries}
            disabled={rebuildingAll || isLoading}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            title="Rebuild pattern cache and earnings summary for all whitelisted affiliates"
          >
            <Database className="h-4 w-4 mr-2" />
            {rebuildingAll ? "Rebuilding All..." : "Rebuild All"}
          </Button>
          <Button
            onClick={() => setShowAddForm(true)}
            className="bg-[#B8460E] hover:bg-[#8B3A0E] text-white"
            disabled={showAddForm || editingId}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Affiliate
          </Button>
        </div>
      </div>

      {/* Update Summary Info Card */}
      <Card className="p-4 bg-blue-500/10 backdrop-blur-xl border border-blue-500/20">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-blue-300 font-semibold mb-1">
              Whitelist Management Tips
            </h4>
            <div className="text-blue-200 text-sm space-y-1">
              <p>
                • <strong>Refresh List:</strong> Click if newly invited users
                don't appear in the whitelist
              </p>
              <p>
                • <strong>Rebuild All:</strong> Use if affiliates show 0 ads but
                have data in the system
              </p>
              <p>
                • <strong>Auto-refresh:</strong> List updates automatically
                every 10 seconds
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Messages */}
      {error && (
        <Alert type="error" onClose={clearMessages}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert type="success" onClose={clearMessages}>
          {success}
        </Alert>
      )}

      {/* Add Form */}
      {showAddForm && (
        <Card className="p-6 bg-white/10 backdrop-blur-xl border border-white/20">
          <form onSubmit={handleAdd} className="space-y-4">
            <h4 className="text-lg font-semibold text-white mb-4">
              Add New Affiliate
            </h4>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                <Users className="inline h-4 w-4 mr-1" />
                Affiliate Name
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.affiliate_name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      affiliate_name: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-3 pr-12 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-[#B8460E]/50 focus:border-[#B8460E]/50 transition-all duration-300"
                  placeholder="Enter affiliate name or select from existing"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white transition-colors"
                >
                  <ChevronDown className="h-5 w-5" />
                </button>

                {showDropdown && existingAffiliates.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-black/90 backdrop-blur-xl border border-white/20 rounded-xl max-h-48 overflow-y-auto">
                    {existingAffiliates.map((affiliate) => (
                      <button
                        key={affiliate}
                        type="button"
                        onClick={() => handleSelectExistingAffiliate(affiliate)}
                        className="w-full px-4 py-2 text-left text-white hover:bg-white/10 transition-colors first:rounded-t-xl last:rounded-b-xl"
                      >
                        {affiliate}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Click the dropdown arrow to select from existing affiliates
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                <Tag className="inline h-4 w-4 mr-1" />
                Ad Name Patterns (comma-separated)
              </label>
              <input
                type="text"
                value={formData.patterns}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, patterns: e.target.value }))
                }
                className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-[#B8460E]/50 focus:border-[#B8460E]/50 transition-all duration-300"
                placeholder="e.g., john, johnads, john-special (optional)"
              />
              <p className="text-xs text-gray-400 mt-1">
                These patterns will be matched against ad names. If empty, only
                exact name matches will work.
              </p>
            </div>

            <div className="flex items-center space-x-3 p-4 bg-blue-500/10 rounded-xl border border-blue-500/20">
              <input
                type="checkbox"
                id="autoReassign"
                checked={formData.autoReassign}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    autoReassign: e.target.checked,
                  }))
                }
                className="w-4 h-4 text-[#B8460E] bg-white/10 border-white/20 rounded focus:ring-[#B8460E] focus:ring-2"
              />
              <label htmlFor="autoReassign" className="text-sm text-blue-200">
                <RefreshCw className="inline h-4 w-4 mr-1" />
                Automatically reassign existing ads that match these patterns
              </label>
            </div>

            <div className="flex space-x-3">
              <Button
                type="submit"
                disabled={isLoading || !formData.affiliate_name.trim()}
                className="bg-[#B8460E] hover:bg-[#8B3A0E] text-white"
              >
                <Save className="h-4 w-4 mr-2" />
                {isLoading ? "Adding..." : "Add Affiliate"}
              </Button>
              <Button
                type="button"
                onClick={cancelEdit}
                className="bg-white/10 hover:bg-white/20 text-white border border-white/20"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Whitelist Table */}
      <Card className="p-6 bg-white/10 backdrop-blur-xl border border-white/20">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-white font-semibold">
                  Affiliate Name
                </th>
                <th className="text-left py-3 px-4 text-white font-semibold">
                  Patterns
                </th>
                <th className="text-left py-3 px-4 text-white font-semibold">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-white font-semibold">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {whitelist.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-white/5 hover:bg-white/5 transition-colors"
                >
                  <td className="py-3 px-4">
                    {editingId === item.id ? (
                      <input
                        type="text"
                        value={formData.affiliate_name}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            affiliate_name: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:ring-2 focus:ring-[#B8460E]/50"
                      />
                    ) : (
                      <span className="text-white font-medium">
                        {item.affiliate_name}
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {editingId === item.id ? (
                      <input
                        type="text"
                        value={formData.patterns}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            patterns: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:ring-2 focus:ring-[#B8460E]/50"
                        placeholder="pattern1, pattern2, pattern3"
                      />
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {item.patterns && item.patterns.length > 0 ? (
                          item.patterns.map((pattern, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-[#B8460E]/20 text-[#B8460E] text-xs rounded-md border border-[#B8460E]/30"
                            >
                              {pattern}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 text-sm">
                            Exact name only
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() =>
                        handleToggleActive(item.id, item.is_active)
                      }
                      className={`flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        item.is_active
                          ? "bg-green-500/20 text-green-300 border border-green-500/30"
                          : "bg-red-500/20 text-red-300 border border-red-500/30"
                      }`}
                    >
                      {item.is_active ? (
                        <>
                          <Eye className="h-3 w-3" />
                          <span>Active</span>
                        </>
                      ) : (
                        <>
                          <EyeOff className="h-3 w-3" />
                          <span>Inactive</span>
                        </>
                      )}
                    </button>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex space-x-2">
                      {editingId === item.id ? (
                        <>
                          <button
                            onClick={handleUpdate}
                            disabled={isLoading}
                            className="p-2 bg-green-500/20 hover:bg-green-500/30 text-green-300 rounded-lg transition-colors"
                            title="Save changes"
                          >
                            <Save className="h-4 w-4" />
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="p-2 bg-gray-500/20 hover:bg-gray-500/30 text-gray-300 rounded-lg transition-colors"
                            title="Cancel"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEdit(item)}
                            disabled={editingId !== null || showAddForm}
                            className="p-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg transition-colors disabled:opacity-50"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() =>
                              handleManualReassign(
                                item.affiliate_name,
                                item.patterns
                              )
                            }
                            disabled={
                              isLoading || editingId !== null || showAddForm
                            }
                            className="p-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded-lg transition-colors disabled:opacity-50"
                            title="Reassign existing ads"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() =>
                              handleRebuildSummary(item.affiliate_name)
                            }
                            disabled={
                              isLoading ||
                              editingId !== null ||
                              showAddForm ||
                              rebuildingIndividual.has(item.affiliate_name)
                            }
                            className="p-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 rounded-lg transition-colors disabled:opacity-50"
                            title="Rebuild summary for this affiliate"
                          >
                            <Database className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            disabled={
                              isLoading || editingId !== null || showAddForm
                            }
                            className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-colors disabled:opacity-50"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {whitelist.length === 0 && !isLoading && (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">No affiliates in whitelist</p>
              <p className="text-gray-500 text-sm">
                Add affiliates to enable automatic data assignment
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Info Card */}
      <Card className="p-6 bg-blue-500/10 backdrop-blur-xl border border-blue-500/20">
        <h4 className="text-lg font-semibold text-blue-300 mb-3">
          How It Works
        </h4>
        <div className="space-y-2 text-blue-200 text-sm">
          <p>
            • <strong>New CSV Imports:</strong> System automatically scans ad
            names for affiliate patterns
          </p>
          <p>
            • <strong>Existing Data:</strong> Use "Auto-reassign" checkbox or
            manual reassign button to update existing ads
          </p>
          <p>
            • <strong>Pattern Matching:</strong> If an ad name contains any
            pattern, it gets assigned to that affiliate
          </p>
          <p>
            • <strong>Exact Matches:</strong> If no patterns specified, only
            exact name matches work
          </p>
          <p>
            • <strong>Status:</strong> Inactive affiliates won't be assigned any
            new data
          </p>
          <p>
            • <strong>Example:</strong> Ad "jor-special-offer-v2" matches
            pattern "jor" → assigned to "jor"
          </p>
          <p>
            • <strong>Dropdown:</strong> Select from existing affiliates in your
            database instead of typing
          </p>
        </div>
      </Card>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Remove from Whitelist"
        message="Are you sure you want to remove this affiliate from the whitelist? This will prevent their ads from being automatically assigned to them."
        confirmText="Remove Affiliate"
        cancelText="Cancel"
        isLoading={isLoading}
        userName={
          itemToDelete
            ? whitelist.find((item) => item.id === itemToDelete)?.affiliate_name
            : null
        }
      />
    </div>
  );
};

export default WhitelistManager;
